import { IS_ANDROID_APP } from '../utils/platform';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { 
  Play, 
  Lock, 
  Crown,
  Star,
  Zap,
  Brain,
  Target,
  BookOpen,
  GraduationCap,
  Video,
  Hash
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREE_MAX_LEVEL = 4;

// Levels above this are moved to /advanced-lab so the default Play page
// stays focused on classic-style solving (NYT-comparable curriculum).
// L11–L17 are rare-technique territory most casual solvers won't engage
// with; they live in a clearly-labeled side area until users explicitly
// opt in.
const CLASSIC_MAX_LEVEL = 10;

// Four technique tiers (matches tutorial curriculum)
const TIERS = [
  {
    key: 'foundations',
    name: 'Foundations',
    subtitle: 'Singles & Pairs',
    range: [1, 4],
    color: '#34C759',
    icon: Star,
    firstTutorial: 1,
  },
  {
    key: 'intermediate',
    name: 'Intermediate',
    subtitle: 'Fish & Wings',
    range: [5, 10],
    color: '#8B5A3C',
    icon: Brain,
    firstTutorial: 5,
  },
  {
    key: 'advanced',
    name: 'Advanced',
    subtitle: 'Single-digit chains',
    range: [11, 13],
    color: '#FF9F0A',
    icon: Target,
    firstTutorial: 11,
  },
  {
    key: 'expert',
    name: 'Expert',
    subtitle: 'Multi-digit logic',
    range: [14, 17],
    color: '#AF52DE',
    icon: Crown,
    firstTutorial: 14,
  },
];

const difficultyIcons = {
  1: Star, 2: Star, 3: Star, 4: Star,
  5: Brain, 6: Brain, 7: Brain, 8: Brain, 9: Brain, 10: Brain,
  11: Target, 12: Target, 13: Target,
  14: Crown, 15: Crown, 16: Crown, 17: Crown,
};

function getTier(level) {
  return TIERS.find((t) => level >= t.range[0] && level <= t.range[1]) || TIERS[0];
}

export default function PlayPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [difficulties, setDifficulties] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lessonCode, setLessonCode] = useState('');

  useEffect(() => {
    const fetchDifficulties = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/difficulties`);
        setDifficulties(data);
      } catch (err) {
        toast.error('Failed to load difficulty levels');
      }
    };
    fetchDifficulties();
  }, []);

  const canAccessLevel = (level) => {
    if (!user) return level <= FREE_MAX_LEVEL;
    const tier = user.subscription_tier;
    if (tier === 'premium' || tier === 'premium_plus') return true;
    return level <= FREE_MAX_LEVEL;
  };

  const startGame = async (level) => {
    if (!canAccessLevel(level)) {
      toast.error('This level requires a Premium subscription');
      navigate('/subscription');
      return;
    }

    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/puzzles/generate`,
        { difficulty_level: level },
        { withCredentials: true }
      );
      navigate(`/play/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate puzzle');
    } finally {
      setGenerating(false);
    }
  };

  function getStartButtonContent() {
    if (generating) {
      return (
        <span className="flex items-center gap-2">
          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          Generating...
        </span>
      );
    }
    if (!canAccessLevel(selectedLevel)) {
      return (
        <span className="flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Unlock Premium
        </span>
      );
    }
    return (
      <span className="flex items-center gap-2">
        <Play className="w-5 h-5" />
        Start Puzzle
      </span>
    );
  }

  // Levels visible on the default Play page. L11+ live on /advanced-lab.
  const classicDifficulties = difficulties.filter((d) => d.id <= CLASSIC_MAX_LEVEL);

  // Group difficulties by technique tier (matches tutorial curriculum)
  const tieredDifficulties = TIERS
    .filter((tier) => tier.range[0] <= CLASSIC_MAX_LEVEL)
    .map((tier) => ({
      ...tier,
      levels: classicDifficulties.filter(
        (d) => d.id >= tier.range[0] && d.id <= Math.min(tier.range[1], CLASSIC_MAX_LEVEL)
      ),
    }));

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Choose Your Challenge
          </h1>
          <p className="text-[var(--text-secondary)] text-lg">
            10 classic difficulty levels — plus an Advanced Lab for power solvers
          </p>
        </div>

        {/* Horizontal Scrolling Difficulty Selector */}
        <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-8">
          <CardHeader>
            <CardTitle className="text-[var(--text-primary)] font-heading">Select Level</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-3 pb-4">
                {classicDifficulties.map((diff) => {
                  const Icon = difficultyIcons[diff.id] || Star;
                  const tier = getTier(diff.id);
                  const locked = !canAccessLevel(diff.id);
                  
                  return (
                    <button
                      key={diff.id}
                      onClick={() => setSelectedLevel(diff.id)}
                      className={`difficulty-pill flex items-center gap-2 ${
                        selectedLevel === diff.id ? 'active' : ''
                      } ${locked ? 'locked' : ''}`}
                      style={{
                        borderColor: selectedLevel === diff.id ? tier.color : undefined,
                        backgroundColor: selectedLevel === diff.id ? `${tier.color}20` : undefined,
                      }}
                      disabled={locked}
                      data-testid={`level-${diff.id}`}
                    >
                      {locked ? (
                        <Lock className="w-4 h-4" />
                      ) : (
                        <Icon className="w-4 h-4" style={{ color: tier.color }} />
                      )}
                      <span className="font-medium">{diff.id}</span>
                    </button>
                  );
                })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Selected Level Info */}
        {selectedLevel && (
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-8" data-testid="selected-level-info">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span 
                      className="text-xs px-2 py-1 rounded font-medium"
                      style={{ 
                        backgroundColor: getTier(selectedLevel).color + '20',
                        color: getTier(selectedLevel).color
                      }}
                    >
                      Level {selectedLevel}
                    </span>
                    <span className="text-xs text-[var(--text-disabled)]">
                      {getTier(selectedLevel).name}
                    </span>
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[var(--text-primary)] mb-2">
                    {difficulties.find(d => d.id === selectedLevel)?.name}
                  </h2>
                  <p className="text-[var(--text-secondary)]">
                    {difficulties.find(d => d.id === selectedLevel)?.description}
                  </p>
                </div>
                <Button
                  onClick={() => startGame(selectedLevel)}
                  disabled={generating || !canAccessLevel(selectedLevel)}
                  className="bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold px-8 py-6 text-lg"
                  data-testid="start-game-btn"
                >
                  {getStartButtonContent()}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Level 0 — Foundational Candidates Tutorial (always free, always first) */}
        <Card 
          className="bg-gradient-to-r from-[#34C759]/15 to-[#8B5A3C]/15 border-[#34C759]/30 mb-8 cursor-pointer hover:border-[#34C759]/60 transition-all"
          onClick={() => navigate('/tutorials/0')}
          data-testid="level-0-tutorial-card"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#34C759]/20 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-6 h-6 text-[#34C759]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-mono text-[#34C759]">Level 0 · Free</span>
                <span className="text-xs px-2 py-0.5 rounded bg-[#34C759]/20 text-[#34C759]">Start here</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)]">
                Reading Candidates
              </h3>
              <p className="text-sm text-[var(--text-secondary)] line-clamp-2">
                Learn pencil marks — the notation every technique above Level 2 depends on.
              </p>
            </div>
            <BookOpen className="w-5 h-5 text-[var(--text-secondary)] flex-shrink-0 hidden sm:block" />
          </CardContent>
        </Card>

        {/* Difficulty Tiers */}
        <div className="space-y-10">
          {tieredDifficulties.map((tier) => {
            if (tier.levels.length === 0) return null;
            
            return (
              <div key={tier.key} data-testid={`tier-${tier.key}`}>
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <h3 
                      className="font-heading text-lg font-semibold flex items-center gap-2"
                      style={{ color: tier.color }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tier.color }} />
                      {tier.name}
                      <span className="font-mono text-xs text-[var(--text-disabled)] font-normal">
                        Levels {tier.range[0]}–{tier.range[1]}
                      </span>
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] ml-4">{tier.subtitle}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/tutorials/${tier.firstTutorial}`)}
                    className="border-[var(--border-subtle)] hover:border-[var(--border-strong)] text-[var(--text-primary)]"
                    style={{ borderColor: `${tier.color}40` }}
                    data-testid={`learn-tier-${tier.key}`}
                  >
                    <BookOpen className="w-4 h-4 mr-2" style={{ color: tier.color }} />
                    Learn techniques
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tier.levels.map((diff) => {
                    const Icon = difficultyIcons[diff.id] || Star;
                    const locked = !canAccessLevel(diff.id);
                    
                    return (
                      <Card 
                        key={diff.id}
                        className={`bg-[var(--background-surface)] border-[var(--border-subtle)] cursor-pointer transition-all hover:border-[var(--border-strong)] ${
                          locked ? 'opacity-60' : ''
                        }`}
                        onClick={() => !locked && setSelectedLevel(diff.id)}
                        data-testid={`technique-card-${diff.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded flex items-center justify-center"
                                style={{ backgroundColor: tier.color + '20' }}
                              >
                                <Icon className="w-4 h-4" style={{ color: tier.color }} />
                              </div>
                              <span className="font-mono text-sm text-[var(--text-disabled)]">
                                Level {diff.id}
                              </span>
                            </div>
                            {locked && <Lock className="w-4 h-4 text-[var(--text-disabled)]" />}
                          </div>
                          <h4 className="font-heading font-semibold text-[var(--text-primary)] mb-1">
                            {diff.name}
                          </h4>
                          <p className="text-sm text-[var(--text-secondary)] line-clamp-2">
                            {diff.description}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Open lesson by code — quick redeem so viewers of a YouTube
            video can jump straight to the puzzle the host is solving. */}
        <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mt-10" data-testid="lesson-code-card">
          <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Video className="w-5 h-5 text-[#8B5A3C]" />
                <h3 className="font-heading font-semibold text-[var(--text-primary)]">
                  Watching a video lesson?
                </h3>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">
                Type the code the host called out (e.g. <code className="font-mono">LSN-260509</code>) to play the same puzzle.
              </p>
            </div>
            <form
              className="flex gap-2 sm:w-auto w-full"
              onSubmit={(e) => {
                e.preventDefault();
                const code = lessonCode.trim().toUpperCase();
                if (!code) return;
                navigate(`/lessons/${encodeURIComponent(code)}`);
              }}
            >
              <div className="relative flex-1 sm:w-56">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  value={lessonCode}
                  onChange={(e) => setLessonCode(e.target.value)}
                  placeholder="LSN-260509"
                  className="pl-9 pr-3 py-2 w-full rounded-md bg-[var(--background-elevated)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)] font-mono text-sm focus:outline-none focus:border-[#8B5A3C]"
                  data-testid="lesson-code-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
                data-testid="lesson-code-go"
              >
                Open
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Advanced Lab entry — clearly separated from the classic ladder */}
        <Card
          className="bg-[var(--background-surface)] border-dashed border-[var(--border-strong)] mt-10 cursor-pointer hover:border-[#AF52DE] transition-all"
          onClick={() => navigate('/advanced-lab')}
          data-testid="advanced-lab-card"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#AF52DE]/15 flex items-center justify-center flex-shrink-0">
              <Crown className="w-6 h-6 text-[#AF52DE]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-mono text-[#AF52DE]">Advanced Lab · Levels 11–17</span>
                <span className="text-xs px-2 py-0.5 rounded bg-[#AF52DE]/15 text-[#AF52DE]">For power solvers</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)]">
                Beyond Classic Sudoku
              </h3>
              <p className="text-sm text-[var(--text-secondary)] line-clamp-2">
                Skyscrapers, kites, colors, AICs, ALSs and forcing chains — quirky, rare techniques that don't show up in NYT-style puzzles. Open the Lab if you're hunting them.
              </p>
            </div>
            <BookOpen className="w-5 h-5 text-[var(--text-secondary)] flex-shrink-0 hidden sm:block" />
          </CardContent>
        </Card>

        {/* Upgrade CTA for free/guest users */}
        {!IS_ANDROID_APP && (!user || user.subscription_tier === 'free') && (
          <Card className="bg-gradient-to-r from-[#8B5A3C]/20 to-[#AF52DE]/20 border-[#8B5A3C]/30 mt-8">
            <CardContent className="p-6 text-center">
              <Crown className="w-10 h-10 text-[#8B5A3C] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-2">
                Unlock Levels 5–10 + the Advanced Lab
              </h3>
              <p className="text-[var(--text-secondary)] mb-4">
                Upgrade to Premium for unlimited puzzles across the classic ladder and the Advanced Lab, plus tutorials and an ad-free experience.
              </p>
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
                data-testid="upgrade-cta"
              >
                View Plans — $4.99/mo
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
